import _ from "underscore";

import type {
  FreelancerJobToIssueInvoice,
  InitialInvoiceData,
} from "@js/apps/invoices/api";
import type { FreelancerInvoice } from "@js/types/invoices";

import type {
  InvoiceFormData,
  InvoiceFormItemData,
} from "../../forms/add-invoice/types";

type BaseInvoiceInitialValues = {
  suggestedInvoiceNumber: InvoiceFormData["number"];
  jobIdParam: number;
  jobSelectList: FreelancerJobToIssueInvoice[];
  issueDate: string;
  wiseFee?: string;
  talentInvoiceEditingDisabled?: boolean;
  offerPayRate?: string;
};

type GetAddInvoiceInitialValues = Omit<
  BaseInvoiceInitialValues,
  "jobIdParam"
> & {
  idOfInvoiceToCopy: string;
  invoiceToCopy?: FreelancerInvoice;
  jobIdParam?: number;
  isExpenseOnlyInvoiceForm?: boolean;
};

type GetInitialValuesForNewInvoiceParams = Omit<
  BaseInvoiceInitialValues,
  "jobIdParam"
> & {
  new_items?: InvoiceFormData["new_items"];
  attachment_ids?: number[];
  notes_to_receipt?: InvoiceFormData["notes_to_receipt"];
  jobIdParam?: number;
};

export const isPONumberRequired = (
  values: Pick<InvoiceFormData, "new_items">,
  initialInvoiceData: InitialInvoiceData | undefined,
) => {
  return !!initialInvoiceData?.po_number_required_since
    ? !!(values.new_items || []).some((item) => item && !item.po_number)
    : false;
};

const EXPENSE_ONLY_INVOICE_NOTES =
  "This is an Expense Only invoice. Braintrust Service Fees do not apply to Expense Only invoices.";

const WISE_FEE_DESCRIPTION = "Wise Fee";
const PROJECT_FEE_DESCRIPTION = "Project Fee";

export const isWiseFeeItem = (item: InvoiceFormItemData): boolean => {
  return item.item_type === ENUMS.InvoiceItemType.WISE_FEE;
};

export const isProjectFeeItem = (item: InvoiceFormItemData): boolean => {
  return item.item_type === ENUMS.InvoiceItemType.PROJECT_FEE;
};

const processInvoiceItemsForWiseFee = ({
  currentInvoiceItems,
  wiseFee,
  issueDate,
}: {
  currentInvoiceItems: InvoiceFormData["new_items"];
  wiseFee?: string;
  issueDate: string;
}) => {
  if (!wiseFee) {
    return currentInvoiceItems?.filter((item) => !isWiseFeeItem(item));
  }

  const hasWiseFeeItem =
    !!currentInvoiceItems &&
    currentInvoiceItems?.some((item) => isWiseFeeItem(item));

  if (hasWiseFeeItem) {
    return currentInvoiceItems.map((item) => {
      if (!isWiseFeeItem(item)) {
        return item;
      }

      // Copied invoice case
      // ensure item detected as wise fee item has correct dates and the actual wise fee value
      return {
        ...item,
        unit_price: wiseFee,
        date_from: issueDate,
        date_to: issueDate,
      };
    });
  }

  const currentInvoiceItemsCopy = currentInvoiceItems
    ? [...currentInvoiceItems]
    : [];
  const wiseFeeItem: InvoiceFormItemData = {
    date_from: issueDate,
    date_to: issueDate,
    description: WISE_FEE_DESCRIPTION,
    quantity: "1",
    unit_price: wiseFee,
    item_type: ENUMS.InvoiceItemType.WISE_FEE,
  };

  return [...currentInvoiceItemsCopy, wiseFeeItem];
};

const processInvoiceItemsForProjectFee = ({
  currentInvoiceItems,
  offerPayRate,
  issueDate,
  talentInvoiceEditingDisabled,
}: {
  currentInvoiceItems: InvoiceFormData["new_items"];
  offerPayRate?: string;
  issueDate: string;
  talentInvoiceEditingDisabled?: boolean;
}) => {
  if (!talentInvoiceEditingDisabled || !offerPayRate) {
    return currentInvoiceItems?.filter((item) => !isProjectFeeItem(item));
  }

  const projectFeeItem: InvoiceFormItemData = {
    date_from: issueDate,
    date_to: issueDate,
    description: PROJECT_FEE_DESCRIPTION,
    quantity: "1",
    unit_price: offerPayRate,
    item_type: ENUMS.InvoiceItemType.PROJECT_FEE,
  };

  return [projectFeeItem];
};

export const getInvoiceItems = ({
  currentInvoiceItems,
  wiseFee,
  issueDate,
  talentInvoiceEditingDisabled,
  offerPayRate,
}: {
  currentInvoiceItems: InvoiceFormData["new_items"];
  wiseFee?: string;
  issueDate: string;
  talentInvoiceEditingDisabled?: boolean;
  offerPayRate?: string;
}): InvoiceFormData["new_items"] => {
  const invoiceItemsProjectFeeProcessed = processInvoiceItemsForProjectFee({
    currentInvoiceItems,
    issueDate,
    offerPayRate,
    talentInvoiceEditingDisabled,
  });

  const invoiceItemsWiseFeeProcessed = processInvoiceItemsForWiseFee({
    wiseFee,
    issueDate,
    currentInvoiceItems: invoiceItemsProjectFeeProcessed,
  });

  return invoiceItemsWiseFeeProcessed;
};

export const getAddInvoiceInitialValues = ({
  idOfInvoiceToCopy,
  invoiceToCopy,
  suggestedInvoiceNumber,
  jobIdParam,
  jobSelectList,
  issueDate,
  isExpenseOnlyInvoiceForm,
  wiseFee,
  talentInvoiceEditingDisabled,
  offerPayRate,
}: GetAddInvoiceInitialValues) => {
  if (idOfInvoiceToCopy && invoiceToCopy?.subject) {
    return getInitialValuesForCopiedInvoice({
      suggestedInvoiceNumber,
      invoiceToCopy,
      issueDate,
      jobSelectList,
      wiseFee,
      talentInvoiceEditingDisabled,
      offerPayRate,
    });
  }

  return getInitialValuesForNewInvoice({
    suggestedInvoiceNumber,
    jobIdParam,
    jobSelectList,
    issueDate,
    wiseFee,
    talentInvoiceEditingDisabled,
    offerPayRate,
    notes_to_receipt: isExpenseOnlyInvoiceForm
      ? EXPENSE_ONLY_INVOICE_NOTES
      : undefined,
  });
};

export const getInitialValuesForNewInvoice = ({
  suggestedInvoiceNumber,
  jobIdParam,
  jobSelectList,
  issueDate,
  new_items,
  attachment_ids,
  notes_to_receipt,
  wiseFee,
  talentInvoiceEditingDisabled,
  offerPayRate,
}: GetInitialValuesForNewInvoiceParams) => {
  const jobId = getJobId({
    jobIdParam,
    jobSelectList,
  });

  return {
    job: jobId,
    employer: jobSelectList.find(({ id }) => jobId === id)?.employer.id,
    issued_at: issueDate,
    number: (jobId && suggestedInvoiceNumber) || "",
    new_items: getInvoiceItems({
      wiseFee,
      currentInvoiceItems: new_items,
      issueDate,
      talentInvoiceEditingDisabled,
      offerPayRate,
    }),
    attachment_ids,
    notes_to_receipt,
  };
};

type GetInitialValuesForCopiedInvoiceParams = Pick<
  BaseInvoiceInitialValues,
  | "issueDate"
  | "suggestedInvoiceNumber"
  | "jobSelectList"
  | "wiseFee"
  | "talentInvoiceEditingDisabled"
  | "offerPayRate"
> &
  Required<Pick<GetAddInvoiceInitialValues, "invoiceToCopy">>;

const getInitialValuesForCopiedInvoice = ({
  issueDate,
  invoiceToCopy,
  suggestedInvoiceNumber,
  jobSelectList,
  wiseFee,
  talentInvoiceEditingDisabled,
  offerPayRate,
}: GetInitialValuesForCopiedInvoiceParams): Partial<InvoiceFormData> => {
  const isJobOnList = jobSelectList.some(
    ({ id }) => invoiceToCopy.subject.id === id,
  );

  const copiedItems: InvoiceFormItemData[] = (invoiceToCopy.items || []).map(
    (item) =>
      _.pick(
        item,
        "description",
        "unit_price",
        "tax_rate",
        "tax_name",
        "quantity",
        "po_number",
        "item_type",
      ),
  );

  return {
    issued_at: issueDate,
    number: suggestedInvoiceNumber || invoiceToCopy.number,
    job: isJobOnList ? invoiceToCopy.subject.id : undefined,
    employer: isJobOnList ? invoiceToCopy.employer.id : undefined,
    new_items: getInvoiceItems({
      currentInvoiceItems: copiedItems,
      wiseFee,
      issueDate,
      talentInvoiceEditingDisabled,
      offerPayRate,
    }),
  };
};

type GetJobIdParams = Partial<
  Pick<BaseInvoiceInitialValues, "jobIdParam" | "jobSelectList">
>;

const getJobId = ({ jobIdParam, jobSelectList }: GetJobIdParams) => {
  // id specified in query param or chosen in form -- invoice for specific job
  if (jobIdParam) {
    return jobIdParam;
  }

  // user can't choose, so we set only option for better UX
  const isOnlyOneJobToSelect = jobSelectList?.length === 1;
  if (isOnlyOneJobToSelect) {
    return jobSelectList[0].id;
  }

  // user needs to choose by himself
  return undefined;
};
